import { Inject, Injectable } from '@angular/core';
import { ConfigService } from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { cloneDeep } from 'lodash';
import { EMPTY } from 'rxjs';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';
import { ServicePartQuestionnaireService } from '../../services/service-part-questionnaire.service';
import { XmlParserService } from '../../services/xml-parser.service';
import { XmlSerializerService } from '../../services/xml-serializer.service';
import { FormsConstants } from '../../types/forms-constants';
import { FormUtilities } from '../../utilities/form-utilities';
import { NotificationUtilities } from '../../utilities/notification-utilities';
import { navigateToForm, updatedXml } from '../actions/form.actions';
import {
    loadSpqDoc,
    loadSpqDocSuccess,
    updateDefectServicePartIdentificationField,
} from '../actions/questionnaire.actions';

@Injectable()
export class ServicePartQuestionnaireEffects {
    public constructor(
        private readonly actions$: Actions,
        private readonly xmlParserService: XmlParserService,
        private readonly xmlSerializerService: XmlSerializerService,
        private readonly servicePartQuestionnaireService: ServicePartQuestionnaireService,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly configService: ConfigService,
    ) {}

    public loadSpqDoc$ = createEffect(() =>
        this.actions$.pipe(
            ofType(loadSpqDoc),
            switchMap((action) =>
                this.servicePartQuestionnaireService.getQuestionnaireFromCache(action.form.formId).pipe(
                    mergeMap((spqModel) => {
                        const spqDoc = cloneDeep(spqModel);

                        this.log.info(
                            `ServicePartQuestionnaireService: getQuestionnaireFromCache: setting spq xml for ${FormUtilities.toString(
                                action.form,
                            )}-${NotificationUtilities.toString(action.notification)}`,
                        );
                        const spqContentResult = this.servicePartQuestionnaireService.setSpqXml(
                            spqDoc.Content,
                            action.notification,
                            action.materialKey,
                        );

                        return [
                            loadSpqDocSuccess({
                                form: action.form,
                                questionnaire: spqDoc,
                                notification: action.notification,
                                language: action.language,
                                contentResults: spqContentResult,
                                materialKey: action.materialKey,
                                lockedBy: this.configService.getUserFrontId(),
                                xml: this.xmlParserService.getXml(undefined),
                                email: this.configService.getCseEmailFax(),
                                materialNumber: action.materialNumber,
                            }),
                            navigateToForm({
                                form: action.form,
                                notification: action.notification,
                                status: FormsConstants.FORMSTATUS.LOCK,
                            }),
                        ];
                    }),
                ),
            ),
        ),
    );

    public loadSpqDocSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(loadSpqDocSuccess),
            switchMap((action) => {
                // the defect service part identification will always be available for the service part questionnaires
                const defectServicePartIdentification = action.contentResults.defectServicePartIdentification;

                if (defectServicePartIdentification) {
                    return [
                        updateDefectServicePartIdentificationField({
                            xmlId: defectServicePartIdentification.materialNo.xmlId,
                            newValue: action.materialNumber,
                        }),
                        updateDefectServicePartIdentificationField({
                            xmlId: defectServicePartIdentification.serialNo.xmlId,
                            newValue: defectServicePartIdentification.serialNo.value,
                        }),
                        updateDefectServicePartIdentificationField({
                            xmlId: defectServicePartIdentification.orderNo.xmlId,
                            newValue: action.notification.qmNum, // add the notification number as order number - required by customer
                        }),
                    ];
                }

                return [];
            }),
            catchError((error) => {
                this.log.error(`FormEffects: loadSpqDocSuccess$: error in pipeline: ${JSON.stringify(error)}`);
                return EMPTY;
            }),
        ),
    );

    public updateDefectServicePartIdentificationField$ = createEffect(() =>
        this.actions$.pipe(
            ofType(updateDefectServicePartIdentificationField),
            map((action) => {
                // Update value in XML parser
                this.xmlSerializerService.updateDefectiveServicePartInputField(action.xmlId, action.newValue);
                return updatedXml({
                    participant: FormsConstants.MASTER_PARTICIPANT_ID, // service part questionnaires are not participant relevant - only master form available
                    xml: this.xmlParserService.getXml(undefined), // passing undefined as doctype because the doctype is only relevant for the astoria forms. SPQ's are only in Cosima format available
                });
            }),
            catchError((error) => {
                this.log.error(
                    `ServicePartQuestionnaireEffects: updateDefectServicePartIdentificationField$: error in pipeline: ${JSON.stringify(
                        error,
                    )}`,
                );
                return EMPTY;
            }),
        ),
    );
}
